import React, { createContext, useContext, useReducer, useState } from "react"
import { Services } from "../enums/Service"
import { ServiceComponentType } from "../enums/ManualTroubleshoot"
import { NPS_SURVEY_POINT } from "../enums/NPS"

export enum ServiceType {
  WIRED = "WIRED",
  WIFI = "WIFI",
  CONNECTION_FAILURE = "CONNECTION_FAILURE",
}

type IContext = {
  goodResult?: boolean
  setGoodResult: (result: boolean) => void

  [Services.Internet]: {
    steps: ServiceComponentType[]
    totalSteps: number
    serviceType?: ServiceType
    endConnection?: NPS_SURVEY_POINT

    [ServiceType.WIFI]: {
      ledLit?: boolean

      steps: ServiceComponentType[]
      liveChat?: boolean
      speedtestResult?: number
      endConnection?: NPS_SURVEY_POINT
    }
    [ServiceType.WIRED]: {
      steps: ServiceComponentType[]
      liveChat?: boolean
      speedtestResult?: number
      endConnection?: NPS_SURVEY_POINT
    }
    [ServiceType.CONNECTION_FAILURE]: {
      steps: ServiceComponentType[]
      liveChat?: boolean
      speedtestResult?: number
      endConnection?: NPS_SURVEY_POINT
    }
    handleAddStep: (content: ServiceComponentType) => void
    wiredInternetService: {
      handleAddStep: (content: ServiceComponentType) => void
      openLiveChat: () => void
      endConnection: (surveypoint: NPS_SURVEY_POINT) => void
    }
    wifiInternetService: {
      handleAddStep: (content: ServiceComponentType) => void
      openLiveChat: () => void
      endConnection: (surveypoint: NPS_SURVEY_POINT) => void
    }

    connectionFailureService: {
      handleAddStep: (content: ServiceComponentType) => void
      // openLiveChat: () => void
      endConnection: (surveypoint: NPS_SURVEY_POINT) => void
    }

    startAgain: () => void
    handleAddServiceType: (type: ServiceType) => void
  }
  InternetService: {
    endConnection: (surveypoint: NPS_SURVEY_POINT) => void
  }
}

const initialContext = {
  setGoodResult: () => {},
  [Services.Internet]: {
    steps: [],
    totalSteps: 1,
    [ServiceType.WIRED]: {
      steps: [],
    },
    [ServiceType.WIFI]: {
      steps: [],
    },
    [ServiceType.CONNECTION_FAILURE]: {
      steps: [],
    },
    handleAddStep: () => {},
    wiredInternetService: {
      handleAddStep: () => {},
      openLiveChat: () => {},
      endConnection: () => {},
    },
    wifiInternetService: {
      handleAddStep: () => {},
      openLiveChat: () => {},
      endConnection: () => {},
    },
    connectionFailureService: {
      handleAddStep: () => {},
      // openLiveChat: () => {},
      endConnection: () => {},
    },
    startAgain: () => {},
    handleAddServiceType: () => {},
  },
  InternetService: {
    endConnection: () => {},
  },
}

const ManualTroubleShootingContext = createContext<IContext>(initialContext)

export const useManualTroubleShootingData = () =>
  useContext(ManualTroubleShootingContext)

enum InternetActionTypes {
  ADD_STEP = "ADD_STEP",
  SET_SERVICE_TYPE = "SET_SERVICE_TYPE",
  RESET = "RESET",
  END_CONNECTION = "END_CONNECTION",
}

enum InternetWiredActionTypes {
  ADD_WIRED_STEP = "ADD_WIRED_STEP",
  UPDATE_SPEEDTESTDATA = "UPDATE_SPEEDTESTDATA",
  GO_TO_LIVECHAT = "GO_TO_LIVECHAT",
  END_CONNECTION = "END_CONNECTION",
}

enum InternetWirelessActionTypes {
  ADD_WIFI_STEP = "ADD_WIFI_STEP",
  GO_TO_LIVECHAT = "GO_TO_LIVECHAT",
  END_CONNECTION = "END_CONNECTION",
}
enum InternetConnectionFailureActionTypes {
  ADD_CONNECTION_FAILURE_STEP = "ADD_CONNECTION_FAILURE_STEP",
  GO_TO_LIVECHAT = "GO_TO_LIVECHAT",
  END_CONNECTION = "END_CONNECTION",
}

type internetAction =
  | {
      type:
        | InternetActionTypes.ADD_STEP
        | InternetWiredActionTypes.ADD_WIRED_STEP
        | InternetWirelessActionTypes.ADD_WIFI_STEP
        | InternetConnectionFailureActionTypes.ADD_CONNECTION_FAILURE_STEP
      payload: { choice: ServiceComponentType }
    }
  | {
      type: InternetWiredActionTypes.UPDATE_SPEEDTESTDATA
      payload: {
        response: number
      }
    }
  | {
      type: InternetActionTypes.SET_SERVICE_TYPE
      payload?: ServiceType
    }
  | {
      type: InternetActionTypes.RESET
      payload?: undefined
    }
  | {
      type:
        | InternetWiredActionTypes.GO_TO_LIVECHAT
        | InternetWirelessActionTypes.GO_TO_LIVECHAT
        | InternetConnectionFailureActionTypes.GO_TO_LIVECHAT
      payload?: undefined
    }
  | {
      type:
        | InternetActionTypes.END_CONNECTION
        | InternetWiredActionTypes.END_CONNECTION
        | InternetWirelessActionTypes.END_CONNECTION
        | InternetConnectionFailureActionTypes.END_CONNECTION
      payload?: {
        surveyPoint: NPS_SURVEY_POINT
      }
    }

type ManualTroubleShootingProviderProps = {
  children: React.ReactNode
}

type wiredState = {
  steps: ServiceComponentType[]
  liveChat?: boolean
  endConnection?: NPS_SURVEY_POINT
}
type wifiState = {
  steps: ServiceComponentType[]
  liveChat?: boolean
  endConnection?: NPS_SURVEY_POINT
}
type connectionFailureState = {
  steps: ServiceComponentType[]
  liveChat?: boolean
  endConnection?: NPS_SURVEY_POINT
}

type internetState = {
  totalSteps: number
  serviceType?: ServiceType
  steps: ServiceComponentType[]
  endConnection?: NPS_SURVEY_POINT

  [ServiceType.WIRED]: wiredState
  [ServiceType.WIFI]: wifiState
  [ServiceType.CONNECTION_FAILURE]: connectionFailureState
}

function InternetReducer(state: internetState, action: internetAction) {
  let newSteps: ServiceComponentType[] = []
  switch (action.type) {
    case InternetActionTypes.SET_SERVICE_TYPE:
      return {
        ...state,
        serviceType: action.payload,
      }
    case InternetActionTypes.ADD_STEP:
      newSteps = [...state.steps]
      newSteps.push(action.payload.choice)
      return {
        ...state,
        steps: newSteps,
        totalSteps: state.totalSteps + 1,
      }
    case InternetActionTypes.END_CONNECTION:
      return {
        ...state,
        endConnection: action.payload?.surveyPoint,
        totalSteps: state.totalSteps + 1,
      }
    case InternetWiredActionTypes.ADD_WIRED_STEP:
      newSteps = [...state[ServiceType.WIRED].steps]
      newSteps.push(action.payload.choice)
      return {
        ...state,
        [ServiceType.WIRED]: {
          ...state[ServiceType.WIRED],

          steps: newSteps,
        },
        totalSteps: state.totalSteps + 1,
      }
    case InternetWirelessActionTypes.ADD_WIFI_STEP:
      newSteps = [...state[ServiceType.WIFI].steps]
      newSteps.push(action.payload.choice)
      return {
        ...state,
        [ServiceType.WIFI]: {
          ...state[ServiceType.WIFI],
          steps: newSteps,
        },
        totalSteps: state.totalSteps + 1,
      }
    case InternetConnectionFailureActionTypes.ADD_CONNECTION_FAILURE_STEP:
      newSteps = [...state[ServiceType.CONNECTION_FAILURE].steps]
      newSteps.push(action.payload.choice)
      return {
        ...state,
        [ServiceType.CONNECTION_FAILURE]: {
          ...state[ServiceType.CONNECTION_FAILURE],
          steps: newSteps,
        },
        totalSteps: state.totalSteps + 1,
      }
    case InternetWiredActionTypes.GO_TO_LIVECHAT:
      return {
        ...state,
        [ServiceType.WIRED]: {
          ...state[ServiceType.WIRED],
          liveChat: true,
        },
        totalSteps: state.totalSteps + 1,
      }
    case InternetWiredActionTypes.END_CONNECTION:
      return {
        ...state,
        [ServiceType.WIRED]: {
          ...state[ServiceType.WIRED],
          endConnection: action.payload?.surveyPoint,
        },
        totalSteps: state.totalSteps + 1,
      }
    case InternetWirelessActionTypes.GO_TO_LIVECHAT:
      return {
        ...state,
        [ServiceType.WIFI]: {
          ...state[ServiceType.WIFI],
          liveChat: true,
        },
        totalSteps: state.totalSteps + 1,
      }
    case InternetWirelessActionTypes.END_CONNECTION:
      return {
        ...state,
        [ServiceType.WIFI]: {
          ...state[ServiceType.WIFI],
          endConnection: action.payload?.surveyPoint,
        },
        totalSteps: state.totalSteps + 1,
      }
    case InternetConnectionFailureActionTypes.END_CONNECTION:
      return {
        ...state,
        [ServiceType.CONNECTION_FAILURE]: {
          ...state[ServiceType.CONNECTION_FAILURE],
          endConnection: action.payload?.surveyPoint,
        },
        totalSteps: state.totalSteps + 1,
      }
    case InternetActionTypes.RESET:
      return {
        ...initialState[Services.Internet],
      }

    default:
      break
  }

  throw Error("Unknown action.")
}

const initialState: { [Services.Internet]: internetState } = {
  [Services.Internet]: {
    totalSteps: 1,

    steps: [ServiceComponentType.REBOOT_EQUIPMENT],
    [ServiceType.WIRED]: {
      steps: [],
    },
    [ServiceType.WIFI]: {
      steps: [],
    },
    [ServiceType.CONNECTION_FAILURE]: {
      steps: [ServiceComponentType.CHECK_UNIFI_EQUIPMENT],
    },
  },
}

export const ManualTroubleShootingProvider: React.FC<
  ManualTroubleShootingProviderProps
> = ({ children }) => {
  const [goodResult, setGoodResult] = useState<boolean>()
  const [ledLit, setLEDLit] = useState(false)
  const [internetState, internetDispatch] = useReducer(
    InternetReducer,
    initialState[Services.Internet]
  )
  //   const [endConnectionState, setEndConnectionState] = useState<boolean>(3000)

  const startAgain = () => {
    internetDispatch({ type: InternetActionTypes.RESET, payload: undefined })
    setGoodResult(undefined)
  }

  const handleAddStep = (choice: ServiceComponentType) => {
    internetDispatch({
      type: InternetActionTypes.ADD_STEP,
      payload: {
        choice,
      },
    })
  }
  const handleAddServiceType = (type: ServiceType) => {
    internetDispatch({
      type: InternetActionTypes.SET_SERVICE_TYPE,
      payload: type,
    })
  }

  const wiredInternetService = {
    handleAddStep: (choice: ServiceComponentType) => {
      internetDispatch({
        type: InternetWiredActionTypes.ADD_WIRED_STEP,
        payload: {
          choice,
        },
      })
    },
    openLiveChat: () => {
      internetDispatch({
        type: InternetWiredActionTypes.GO_TO_LIVECHAT,
        payload: undefined,
      })
    },
    endConnection: (surveyPoint: NPS_SURVEY_POINT) => {
      handleAddStep(ServiceComponentType.END_CONNECTION)
      internetDispatch({
        type: InternetWiredActionTypes.END_CONNECTION,
        payload: {
          surveyPoint,
        },
      })
    },
  }

  const wifiInternetService = {
    handleAddStep: (choice: ServiceComponentType) => {
      internetDispatch({
        type: InternetWirelessActionTypes.ADD_WIFI_STEP,
        payload: {
          choice,
        },
      })
    },
    openLiveChat: () => {
      internetDispatch({
        type: InternetWirelessActionTypes.GO_TO_LIVECHAT,
        payload: undefined,
      })
    },
    endConnection: (surveyPoint: NPS_SURVEY_POINT) => {
      handleAddStep(ServiceComponentType.END_CONNECTION)

      internetDispatch({
        type: InternetWirelessActionTypes.END_CONNECTION,
        payload: {
          surveyPoint,
        },
      })
    },
  }
  const connectionFailureService = {
    handleAddStep: (choice: ServiceComponentType) => {
      internetDispatch({
        type: InternetConnectionFailureActionTypes.ADD_CONNECTION_FAILURE_STEP,
        payload: {
          choice,
        },
      })
    },
    // openLiveChat: () => {
    //   internetDispatch({
    //     type: InternetConnectionFailureActionTypes.GO_TO_LIVECHAT,
    //     payload: undefined,
    //   })
    // },
    endConnection: (surveyPoint: NPS_SURVEY_POINT) => {
      connectionFailureService.handleAddStep(
        ServiceComponentType.END_CONNECTION
      )

      internetDispatch({
        type: InternetConnectionFailureActionTypes.END_CONNECTION,
        payload: {
          surveyPoint,
        },
      })
    },
  }

  const InternetService = {
    endConnection: (surveyPoint: NPS_SURVEY_POINT) => {
      handleAddStep(ServiceComponentType.END_CONNECTION)

      internetDispatch({
        type: InternetActionTypes.END_CONNECTION,
        payload: {
          surveyPoint,
        },
      })
    },
  }

  const context = React.useMemo(
    () => ({
      [Services.Internet]: {
        ...internetState,

        handleAddStep,
        wiredInternetService,
        wifiInternetService,
        connectionFailureService,
        handleAddServiceType,
        startAgain,
      },
      InternetService,
      goodResult,
      setGoodResult,
    }),
    [
      ...Object.keys(internetState),
      startAgain,
      handleAddStep,
      wiredInternetService,
      connectionFailureService,
      wifiInternetService,
      InternetService,
      handleAddServiceType,
      setGoodResult,
    ]
  )
  return (
    <ManualTroubleShootingContext.Provider value={context}>
      {children}
    </ManualTroubleShootingContext.Provider>
  )
}
